/**
 * Connect-Four engine.
 *
 * Two-player perfect-information drop game on a 7×6 grid. Players
 * pick a column; the piece settles in the lowest empty cell. First
 * to four-in-a-row in any direction (horizontal, vertical, or
 * diagonal) wins. State mirrors tic-tac-toe so the wire format
 * stays compact:
 *   { board: (0|1|null)[42], turnIndex, players, terminal }
 *   move: { column: 0..6 }
 */
import type { UserId } from '@chat/protocol';
import type { GameComponentProps, GameEngine, GameTerminal } from './engine.js';

const COLS = 7;
const ROWS = 6;

export interface ConnectFourState {
  /** Row-major: index = row * COLS + col. Row 0 is the top. */
  readonly board: readonly (number | null)[];
  readonly turnIndex: number;
  readonly players: readonly UserId[];
  readonly terminal: GameTerminal | null;
}

export interface ConnectFourMove {
  readonly column: number;
}

function cellIndex(row: number, col: number): number {
  return row * COLS + col;
}

/** First empty row from the bottom in `column`, or -1 if full. */
function lowestEmptyRow(board: readonly (number | null)[], column: number): number {
  for (let row = ROWS - 1; row >= 0; row--) {
    if (board[cellIndex(row, column)] === null) return row;
  }
  return -1;
}

function evaluateWin(
  board: readonly (number | null)[],
  players: readonly UserId[],
): GameTerminal | null {
  const directions: ReadonlyArray<readonly [number, number]> = [
    [0, 1], // horizontal
    [1, 0], // vertical
    [1, 1], // diagonal ↘
    [1, -1], // diagonal ↙
  ];
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const start = board[cellIndex(row, col)];
      if (start === null || start === undefined) continue;
      for (const [dr, dc] of directions) {
        const endRow = row + 3 * dr;
        const endCol = col + 3 * dc;
        if (endRow < 0 || endRow >= ROWS || endCol < 0 || endCol >= COLS) continue;
        let ok = true;
        for (let i = 1; i < 4; i++) {
          if (board[cellIndex(row + i * dr, col + i * dc)] !== start) {
            ok = false;
            break;
          }
        }
        if (ok) {
          const winner = players[start];
          if (winner) return { winner };
        }
      }
    }
  }
  if (board.every((cell) => cell !== null)) return { draw: true };
  return null;
}

export const connectFour: GameEngine<ConnectFourState, ConnectFourMove> = {
  id: 'connect-four',
  displayName: 'Connect Four',
  minPlayers: 2,
  maxPlayers: 2,

  initialState(players) {
    if (players.length < 2) throw new Error('connect-four needs two players');
    return {
      board: Array.from({ length: ROWS * COLS }, () => null),
      turnIndex: 0,
      players: players.slice(0, 2),
      terminal: null,
    };
  },

  validateMove(state, move, by) {
    if (state.terminal) return 'game is over';
    const playerIndex = state.players.indexOf(by);
    if (playerIndex < 0) return 'spectator — not your game';
    if (playerIndex !== state.turnIndex) return 'not your turn';
    if (!Number.isInteger(move.column) || move.column < 0 || move.column >= COLS) {
      return 'invalid column';
    }
    if (lowestEmptyRow(state.board, move.column) < 0) return 'column is full';
    return null;
  },

  applyMove(state, move, by) {
    const playerIndex = state.players.indexOf(by);
    const row = lowestEmptyRow(state.board, move.column);
    const board = state.board.slice();
    board[cellIndex(row, move.column)] = playerIndex;
    const terminal = evaluateWin(board, state.players);
    return {
      ...state,
      board,
      turnIndex: terminal ? state.turnIndex : (state.turnIndex + 1) % state.players.length,
      terminal,
    };
  },

  currentTurn(state) {
    if (state.terminal) return null;
    return state.players[state.turnIndex] ?? null;
  },

  terminal(state) {
    return state.terminal;
  },

  isPlayer(state, user) {
    return state.players.includes(user);
  },

  Component: ConnectFourBoard,
};

function ConnectFourBoard({
  state,
  self,
  onMove,
  disabled,
}: GameComponentProps<ConnectFourState, ConnectFourMove>) {
  const myIndex = state.players.indexOf(self);
  const spectator = myIndex < 0;
  const canMove = !disabled && !spectator && !state.terminal && state.turnIndex === myIndex;

  const statusText = state.terminal
    ? state.terminal.draw
      ? 'draw'
      : state.terminal.winner === self
        ? 'you won'
        : 'you lost'
    : spectator
      ? 'spectating'
      : canMove
        ? 'your turn'
        : "waiting for opponent's move";

  return (
    <section data-testid="c4-board">
      <p data-testid="c4-status">{statusText}</p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${String(COLS)}, 36px)`,
          gap: 2,
          background: '#1d4ed8',
          padding: 4,
          borderRadius: 6,
          width: 'max-content',
        }}
      >
        {state.board.map((cell, i) => {
          const row = Math.floor(i / COLS);
          const col = i % COLS;
          const colFull = lowestEmptyRow(state.board, col) < 0;
          const colorFor = cell === null ? '#0b1e44' : cell === 0 ? '#ef4444' : '#facc15';
          return (
            <button
              key={i}
              data-testid={`c4-cell-${String(row)}-${String(col)}`}
              type="button"
              disabled={!canMove || colFull}
              onClick={() => {
                if (!canMove || colFull) return;
                onMove({ column: col });
              }}
              aria-label={`column ${String(col + 1)} row ${String(row + 1)}`}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: 'none',
                background: colorFor,
                cursor: canMove && !colFull ? 'pointer' : 'default',
                padding: 0,
              }}
            />
          );
        })}
      </div>
    </section>
  );
}
